import { useCallback, useEffect, useRef, useState } from 'react';

import { Prompt } from 'react-router';
import { toast } from 'react-toastify';

import { useFormik } from 'formik';

import NibLoader from '../NibLoader/NibLoader';

const useStateWithPromise = (initialState) => {
  const [state, setState] = useState(initialState);
  const resolverRef = useRef(null);

  useEffect(() => {
    if (resolverRef.current) {
      resolverRef.current(state);
      resolverRef.current = null;
    }
    /**
     * Since a state update could be triggered with the exact same state again,
     * it's not enough to specify state as the only dependency of this useEffect.
     * That's why resolverRef.current is also a dependency, because it will guarantee,
     * that handleSetState was called in previous render
     */
  }, [resolverRef.current, state]);

  const handleSetState = useCallback(
    (stateAction) => {
      setState(stateAction);
      return new Promise((resolve) => {
        resolverRef.current = resolve;
      });
    },
    [setState],
  );

  return [state, handleSetState];
};

const FormikForm = ({
  validationSchema,
  initialValues,
  children,
  onSubmit,
  data = {},
  enableReinitialize = false,
  formClassName = '',
  ...props
}) => {
  const [isDataSet, toggleDataSet] = useState(false);
  const [isLoading, setLoading] = useStateWithPromise(false);
  const [notSubmitted, setNotSubmitted] = useState(false);

  const formik = useFormik({
    validationSchema,
    initialValues,
    onSubmit,
    enableReinitialize,
    validateOnChange: (val) => {
      setNotSubmitted(true);
      return val;
    },
  });

  const preSubmit = () => {
    for (const key in formik.errors) {
      formik.setFieldTouched(key, true);
    }
    if (Object.keys(formik.errors).length >= 1) {
      console.log(formik.errors);
      toast.error(`Please fill all required fields`);
    }
  };

  /*
   * We can use "enableReinitialize" and pass new async data
   * through "initialValues", but in this case user will see just empty form
   * for a few secs. and we don`t have ability to handle "Loading",
   * that`s why we use custom async useStateWithPromise() and setting all values manually
   */
  const setData = async (data) => {
    if (Object.keys(data).length) {
      if (!isDataSet) await setLoading(true);

      for (const key in data) {
        if (formik.values.hasOwnProperty(key)) {
          await formik.setFieldValue(key, data[key] === null ? '' : data[key]);
        }
      }

      if (!isDataSet) {
        toggleDataSet(true);
        await setLoading(false);
      }
    }
  };

  useEffect(() => {
    setData(data);
  }, [data]);

  useEffect(() => {
    let onBeforeUnload;
    window.addEventListener(
      'beforeunload',
      (onBeforeUnload = function (e) {
        if (notSubmitted) {
          e.preventDefault();
          e.returnValue = '';
        }
      }),
    );
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  });

  const handleSubmit = useCallback(
    (data) => {
      formik.handleSubmit(data);
      setNotSubmitted(false);
    },
    [formik],
  );

  return (
    <>
      <form onSubmit={handleSubmit} className={'form ' + formClassName} {...props}>
        {isLoading && <NibLoader line={false} />}
        {children(
          {
            ...formik,
            handleChange: (props) => {
              setNotSubmitted(true);
              return formik.handleChange(props);
            },
          },
          preSubmit,
        )}
      </form>
      <Prompt
        when={notSubmitted}
        message={(location) => `You have unsaved changes, are you sure you want to continue?`}
      />
    </>
  );
};

export default FormikForm;
